/**
 * Language Context - Contexto para gestión de idiomas
 *
 * Proporciona:
 * - Detección automática del idioma del navegador
 * - Persistencia en localStorage
 * - Cambio de idioma en tiempo real
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Language, TranslationKeys } from './types';
import { es } from './es';
import { en } from './en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const translations: Record<Language, TranslationKeys> = { es, en };

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'fernandoolea-language';

/**
 * Detecta el idioma preferido del navegador
 */
function detectBrowserLanguage(): Language {
  // Verificar si hay un idioma guardado en localStorage
  const savedLanguage = localStorage.getItem(STORAGE_KEY);
  if (savedLanguage === 'es' || savedLanguage === 'en') {
    return savedLanguage;
  }

  // Detectar idioma del navegador
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || 'es';
  const primaryLang = browserLang.split('-')[0].toLowerCase();

  // Si el idioma primario es español, usar español
  if (primaryLang === 'es') {
    return 'es';
  }

  // Para cualquier otro idioma, usar inglés como fallback
  return 'en';
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Solo ejecutar detección en el cliente
    if (typeof window !== 'undefined') {
      return detectBrowserLanguage();
    }
    return 'es';
  });

  // Actualizar el atributo lang del HTML
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook para acceder al contexto de idioma
 */
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
